import { Slot } from '@radix-ui/react-slot'
import React, { useEffect, useState } from 'react'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface OverflowTooltipProps {
  /**
   * Tooltip content. Defaults to `children` when omitted — useful when child
   * renders the same text we want shown on hover.
   */
  text?: React.ReactNode
  children: React.ReactElement
}

/**
 * Wraps a single child and shows a tooltip with its text only when the child
 * actually overflows (scrollWidth > clientWidth). The child must apply its
 * own truncation styles (e.g. ellipsis + max-width) for overflow to occur.
 *
 * Uses a callback ref so the observer rebinds correctly when the trigger
 * remounts under the Radix Tooltip wrapper.
 */
export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
  text,
  children,
}) => {
  const [el, setEl] = useState<HTMLElement | null>(null)
  const [overflow, setOverflow] = useState(false)

  useEffect(() => {
    if (!el) return
    const check = () => setOverflow(el.scrollWidth > el.clientWidth)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [el])

  const trigger = <Slot ref={setEl}>{children}</Slot>

  if (!overflow) return trigger

  return (
    <Tooltip>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      <TooltipContent>{text ?? children}</TooltipContent>
    </Tooltip>
  )
}
